import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { regionService } from '@/services/region.service'
import { useConfirm } from '@/hooks/useConfirm'
import { AdminLayout } from '@/components/AdminLayout'

export function RegionManagementPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const { confirm, modal: confirmModal } = useConfirm()
  const queryClient = useQueryClient()

  const [selectedStateId, setSelectedStateId] = useState('')

  const [showRegionForm, setShowRegionForm] = useState(false)
  const [showCityForm, setShowCityForm] = useState(false)
  const [showAreaForm, setShowAreaForm] = useState(false)

  const [selectedRegion, setSelectedRegion] = useState<any>(null)
  const [selectedCity, setSelectedCity] = useState<any>(null)

  const [newRegion, setNewRegion] = useState({ name: '' })
  const [newCity, setNewCity] = useState({ name: '', regionId: '' })
  const [newArea, setNewArea] = useState({ name: '', cityId: '', description: '' })

  // Fetch states (Bundesländer)
  const { data: statesData } = useQuery({
    queryKey: ['states'],
    queryFn: () => regionService.listStates(token!),
    enabled: !!token,
  })

  // Fetch regions
  const { data: regionsData, isLoading: loadingRegions } = useQuery({
    queryKey: ['regions', selectedStateId],
    queryFn: () => regionService.listRegions(selectedStateId, token!),
    enabled: !!token && !!selectedStateId,
  })

  // Fetch cities for selected region
  const { data: citiesData } = useQuery({
    queryKey: ['cities', selectedRegion?.id],
    queryFn: () => regionService.listCities({ regionId: selectedRegion.id }, token!),
    enabled: !!token && !!selectedRegion,
  })

  // Fetch areas for selected city
  const { data: areasData } = useQuery({
    queryKey: ['areas', selectedCity?.id],
    queryFn: () => regionService.listAreas({ cityId: selectedCity.id }, token!),
    enabled: !!token && !!selectedCity,
  })

  // Create region mutation
  const createRegionMutation = useMutation({
    mutationFn: () =>
      regionService.createRegion({ name: newRegion.name, stateId: selectedStateId }, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regions'] })
      setNewRegion({ name: '' })
      setShowRegionForm(false)
    },
  })

  // Create city mutation
  const createCityMutation = useMutation({
    mutationFn: () =>
      regionService.createCity(
        {
          name: newCity.name,
          regionId: newCity.regionId,
          stateId: selectedStateId,
        },
        token!
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] })
      setNewCity({ name: '', regionId: '' })
      setShowCityForm(false)
    },
  })

  // Create area mutation
  const createAreaMutation = useMutation({
    mutationFn: () =>
      regionService.createArea(
        {
          name: newArea.name,
          cityId: newArea.cityId,
          description: newArea.description,
          isActive: true,
        },
        token!
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas'] })
      setNewArea({ name: '', cityId: '', description: '' })
      setShowAreaForm(false)
    },
  })

  // Delete area mutation
  const deleteAreaMutation = useMutation({
    mutationFn: (areaId: string) => regionService.deleteArea(areaId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas'] })
    },
  })

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.regionManagement')}</h1>
          <p className="text-gray-600">{t('admin.manageRegionsDesc')}</p>
        </div>
        {/* State (Bundesland) Selection */}
        <div className="bg-white rounded-lg shadow p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('admin.state')}
          </label>
          <select
            value={selectedStateId}
            onChange={(e) => {
              setSelectedStateId(e.target.value)
              setSelectedRegion(null)
              setSelectedCity(null)
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">{t('admin.selectState')}</option>
            {(statesData?.states as any[])?.map((state: any) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Regions Column */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">{t('admin.regions')}</h2>
              <button
                onClick={() => setShowRegionForm(!showRegionForm)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {showRegionForm ? t('common.cancel') : `+ ${t('common.create')}`}
              </button>
            </div>

            {/* Add Region Form */}
            {showRegionForm && (
              <div className="p-4 border-b border-gray-200 bg-blue-50">
                <input
                  type="text"
                  placeholder={t('admin.regionName')}
                  value={newRegion.name}
                  onChange={(e) => setNewRegion({ name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                />
                <button
                  onClick={() => createRegionMutation.mutate()}
                  disabled={!newRegion.name || createRegionMutation.isPending}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createRegionMutation.isPending ? t('admin.creating') : t('admin.createRegion')}
                </button>
              </div>
            )}

            {/* Region List */}
            <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
              {loadingRegions ? (
                <div className="p-4 text-center text-gray-600">{t('common.loading')}</div>
              ) : (regionsData?.regions as any[]) && (regionsData.regions as any[]).length > 0 ? (
                (regionsData.regions as any[]).map((region: any) => (
                  <button
                    key={region.id}
                    onClick={() => {
                      setSelectedRegion(region)
                      setSelectedCity(null)
                    }}
                    className={`w-full p-4 text-left hover:bg-gray-50 ${
                      selectedRegion?.id === region.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{region.name}</div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-600">{t('admin.noRegionsYet')}</div>
              )}
            </div>
          </div>

          {/* Cities Column */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">{t('admin.cities')}</h2>
              {selectedRegion && (
                <button
                  onClick={() => {
                    setShowCityForm(!showCityForm)
                    setNewCity({ ...newCity, regionId: selectedRegion.id })
                  }}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {showCityForm ? t('common.cancel') : `+ ${t('common.create')}`}
                </button>
              )}
            </div>

            {/* Add City Form */}
            {showCityForm && selectedRegion && (
              <div className="p-4 border-b border-gray-200 bg-blue-50">
                <input
                  type="text"
                  placeholder={t("admin.cityName")}
                  value={newCity.name}
                  onChange={(e) => setNewCity({ ...newCity, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                />
                <button
                  onClick={() => createCityMutation.mutate()}
                  disabled={!newCity.name || createCityMutation.isPending}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createCityMutation.isPending ? t('admin.creating') : t('admin.createCity')}
                </button>
              </div>
            )}

            {/* City List */}
            <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
              {!selectedRegion ? (
                <div className="p-4 text-center text-gray-600">{t('admin.selectRegionFirst')}</div>
              ) : (citiesData?.cities as any[]) && (citiesData.cities as any[]).length > 0 ? (
                (citiesData.cities as any[]).map((city: any) => (
                  <button
                    key={city.id}
                    onClick={() => setSelectedCity(city)}
                    className={`w-full p-4 text-left hover:bg-gray-50 ${
                      selectedCity?.id === city.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{city.name}</div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-600">{t('admin.noCitiesYet')}</div>
              )}
            </div>
          </div>

          {/* Areas Column */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">{t('admin.areas')}</h2>
              {selectedCity && (
                <button
                  onClick={() => {
                    setShowAreaForm(!showAreaForm)
                    setNewArea({ ...newArea, cityId: selectedCity.id })
                  }}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {showAreaForm ? t('common.cancel') : `+ ${t('common.create')}`}
                </button>
              )}
            </div>

            {/* Add Area Form */}
            {showAreaForm && selectedCity && (
              <div className="p-4 border-b border-gray-200 bg-blue-50">
                <input
                  type="text"
                  placeholder={t("admin.areaName")}
                  value={newArea.name}
                  onChange={(e) => setNewArea({ ...newArea, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                />
                <textarea
                  placeholder={t("admin.description")}
                  value={newArea.description}
                  onChange={(e) => setNewArea({ ...newArea, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                  rows={2}
                />
                <button
                  onClick={() => createAreaMutation.mutate()}
                  disabled={!newArea.name || createAreaMutation.isPending}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createAreaMutation.isPending ? t('admin.creating') : t('admin.createArea')}
                </button>
              </div>
            )}

            {/* Area List */}
            <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
              {!selectedCity ? (
                <div className="p-4 text-center text-gray-600">{t('admin.selectCityFirst')}</div>
              ) : (areasData?.areas as any[]) && (areasData.areas as any[]).length > 0 ? (
                (areasData.areas as any[]).map((area: any) => (
                  <div key={area.id} className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{area.name}</div>
                      {area.description && (
                        <div className="text-sm text-gray-600 mt-1">{area.description}</div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {area.isActive ? t('admin.active') : t('admin.inactive')}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const ok = await confirm({
                          title: t('common.delete'),
                          message: t('admin.confirmDeleteArea', { name: area.name }),
                          confirmLabel: t('common.delete'),
                          cancelLabel: t('common.cancel'),
                          variant: 'danger',
                        })
                        if (ok) {
                          deleteAreaMutation.mutate(area.id)
                        }
                      }}
                      disabled={deleteAreaMutation.isPending}
                      className="ml-2 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-600">{t('admin.noAreasYet')}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {confirmModal}
    </AdminLayout>
  )
}
